import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Building2, 
  PieChart, 
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  ChevronRight,
  Brain,
  Loader2,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Target,
  CreditCard,
  Home,
  Briefcase,
  Calendar
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";
import ReactMarkdown from 'react-markdown';

const EXPENSE_COLORS = {
  cable: "#10B981",
  electric: "#F59E0B",
  water: "#3B82F6",
  garbage: "#8B5CF6",
  food: "#EC4899",
  car_payment: "#EF4444",
  insurance: "#14B8A6",
  night_out: "#F97316",
  maintenance: "#6366F1",
  other: "#6B7280"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function Dashboard() {
  const { api } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/summary");
      setSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const runAiAnalysis = async () => {
    setAiLoading(true);
    setAiDialogOpen(true);
    try {
      const res = await api.post("/ai/financial-analysis");
      setAiAnalysis(res.data);
    } catch (error) {
      console.error("AI analysis failed:", error);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const load = async () => {
      try {
        const res = await api.get("/dashboard/summary");
        if (mounted) {
          setSummary(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch summary:", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    load();
    
    return () => {
      mounted = false;
    };
  }, [api]);

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const expenseData = summary?.expense_breakdown 
    ? Object.entries(summary.expense_breakdown).map(([name, value]) => ({
        name: name.replace('_', ' '),
        value,
        color: EXPENSE_COLORS[name] || "#6B7280"
      }))
    : [];

  // Simulated monthly data for the chart
  const monthlyData = [
    { month: 'Jan', income: 8500, expenses: 6200 },
    { month: 'Feb', income: 9200, expenses: 5800 },
    { month: 'Mar', income: 8800, expenses: 6500 },
    { month: 'Apr', income: 10500, expenses: 7200 },
    { month: 'May', income: 11200, expenses: 6800 },
    { month: 'Jun', income: summary?.total_income || 12000, expenses: summary?.total_expenses || 7500 },
  ];

  return (
    <Layout title="Dashboard">
      <div className="space-y-6">
        {/* AI Financial Analysis Button */}
        <div className="flex justify-end">
          <Button
            onClick={runAiAnalysis}
            className="bg-gradient-to-r from-violet-600 to-primary hover:from-violet-700 hover:to-primary/90 text-white font-bold"
            data-testid="ai-analysis-btn"
          >
            <Brain className="w-5 h-5 mr-2" />
            AI Financial Analysis
          </Button>
        </div>

        {/* Top Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Net Worth */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none card-hover" data-testid="net-worth-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Net Worth</p>
                  <p className="text-3xl font-bold text-white font-mono">
                    {formatCurrency(summary?.net_worth || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2 text-sm">
                <ArrowUpRight className="w-4 h-4 text-primary" />
                <span className="text-primary font-mono">+12.5%</span>
                <span className="text-zinc-500">vs last month</span>
              </div>
            </CardContent>
          </Card>

          {/* Cash Flow */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none card-hover" data-testid="cash-flow-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Cash Flow</p>
                  <p className={`text-3xl font-bold font-mono ${(summary?.net_cash_flow || 0) >= 0 ? 'text-primary' : 'text-red-500'}`}>
                    {formatCurrency(summary?.net_cash_flow || 0)}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded flex items-center justify-center ${(summary?.net_cash_flow || 0) >= 0 ? 'bg-primary/20' : 'bg-red-500/20'}`}>
                  <DollarSign className={`w-5 h-5 ${(summary?.net_cash_flow || 0) >= 0 ? 'text-primary' : 'text-red-500'}`} />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="text-zinc-500">Income:</span>
                <span className="text-primary font-mono">{formatCurrency(summary?.total_income || 0)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Property Value */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none card-hover" data-testid="property-value-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Real Estate</p>
                  <p className="text-3xl font-bold text-amber-500 font-mono">
                    {formatCurrency(summary?.total_property_value || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded bg-amber-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-amber-500" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="text-zinc-500">{summary?.property_count || 0} properties</span>
              </div>
            </CardContent>
          </Card>

          {/* Investments */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none card-hover" data-testid="investments-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Investments</p>
                  <p className="text-3xl font-bold text-violet-500 font-mono">
                    {formatCurrency(summary?.total_investment_value || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded bg-violet-500/20 flex items-center justify-center">
                  <PieChart className="w-5 h-5 text-violet-500" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="text-zinc-500">{summary?.investment_count || 0} holdings</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Income vs Expenses Chart */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none lg:col-span-2" data-testid="income-expenses-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                Income vs Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <defs>
                      <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="month" 
                      stroke="#52525B" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={{ stroke: '#27272A' }}
                    />
                    <YAxis 
                      stroke="#52525B" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={{ stroke: '#27272A' }}
                      tickFormatter={(value) => `$${value / 1000}k`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#0A0A0A', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '0',
                        fontSize: '12px',
                        fontFamily: 'JetBrains Mono'
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="income" 
                      stroke="#10B981" 
                      fillOpacity={1} 
                      fill="url(#incomeGradient)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="expenses" 
                      stroke="#EF4444" 
                      fillOpacity={1} 
                      fill="url(#expenseGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span className="text-sm text-zinc-400">Income</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm text-zinc-400">Expenses</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expense Breakdown */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="expense-breakdown-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                Expense Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expenseData.length > 0 ? (
                <>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={expenseData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {expenseData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            background: '#0A0A0A', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '0',
                            fontSize: '12px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 mt-2">
                    {expenseData.slice(0, 4).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-zinc-400 capitalize">{item.name}</span>
                        </div>
                        <span className="font-mono text-white">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-48 flex items-center justify-center text-zinc-500">
                  No expense data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Loans & HELOC Summary */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="loans-summary-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-red-500" />
                  <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                    Loans & HELOC
                  </CardTitle>
                </div>
                <Link to="/loans">
                  <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Total Balance</p>
                  <p className="text-2xl font-bold text-red-500 font-mono">
                    {formatCurrency(summary?.total_loan_balance || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Monthly Payment</p>
                  <p className="text-2xl font-bold text-amber-500 font-mono">
                    {formatCurrency(summary?.total_loan_payment || 0)}
                  </p>
                </div>
              </div>
              {summary?.loans_summary && summary.loans_summary.length > 0 ? (
                <div className="space-y-2">
                  {summary.loans_summary.map((loan, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 truncate max-w-[150px]">{loan.name}</span>
                      <span className="font-mono text-white">{formatCurrency(loan.balance)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">
                  {summary?.loan_count || 0} active loans
                </p>
              )}
            </CardContent>
          </Card>

          {/* Insurance Summary */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="insurance-summary-card">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-500" />
                <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                  Life Insurance
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Death Benefit</p>
                  <p className="text-2xl font-bold text-white font-mono">
                    {formatCurrency(summary?.total_insurance_death_benefit || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Cash Value</p>
                  <p className="text-2xl font-bold text-teal-500 font-mono">
                    {formatCurrency(summary?.total_insurance_cash_value || 0)}
                  </p>
                </div>
              </div>
              <p className="text-sm text-zinc-500 mt-3">
                {summary?.insurance_count || 0} active policies
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Total Expenses with Loans */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-expenses-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
              Total Monthly Expenses (Including Loan Payments)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-500 font-mono mb-4">
              {formatCurrency(summary?.total_expenses_with_loans || summary?.total_expenses || 0)}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Regular Expenses</p>
                <p className="font-mono text-white">{formatCurrency(summary?.total_expenses || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase">Loan Payments</p>
                <p className="font-mono text-amber-500">{formatCurrency(summary?.total_loan_payment || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase">Net Cash Flow</p>
                <p className={`font-mono ${(summary?.net_cash_flow || 0) >= 0 ? 'text-primary' : 'text-red-500'}`}>
                  {formatCurrency(summary?.net_cash_flow || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase">% of Income</p>
                <p className="font-mono text-white">
                  {summary?.total_income 
                    ? `${(((summary.total_expenses_with_loans || summary.total_expenses) / summary.total_income) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Financial Analysis Dialog */}
        <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2 text-xl">
                <Sparkles className="w-6 h-6 text-primary" />
                AI Financial Analysis Report
              </DialogTitle>
            </DialogHeader>
            
            {aiLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                <p className="text-zinc-400 text-lg">Analyzing your complete financial picture...</p>
                <p className="text-zinc-500 text-sm mt-2">This may take a moment</p>
              </div>
            ) : aiAnalysis ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded">
                    <p className="text-xs text-red-400 uppercase font-mono">Total Debt</p>
                    <p className="text-2xl font-bold text-red-500 font-mono">
                      {formatCurrency(aiAnalysis.summary?.total_debt_balance || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded">
                    <p className="text-xs text-amber-400 uppercase font-mono">Monthly Payments</p>
                    <p className="text-2xl font-bold text-amber-500 font-mono">
                      {formatCurrency(aiAnalysis.summary?.total_monthly_debt_payment || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded">
                    <p className="text-xs text-orange-400 uppercase font-mono">Monthly Interest</p>
                    <p className="text-2xl font-bold text-orange-500 font-mono">
                      {formatCurrency(aiAnalysis.summary?.total_monthly_interest || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded">
                    <p className="text-xs text-primary uppercase font-mono">Monthly Income</p>
                    <p className="text-2xl font-bold text-primary font-mono">
                      {formatCurrency(aiAnalysis.summary?.total_monthly_income || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-teal-500/10 border border-teal-500/30 rounded">
                    <p className="text-xs text-teal-400 uppercase font-mono">Net Worth</p>
                    <p className={`text-2xl font-bold font-mono ${(aiAnalysis.summary?.net_worth || 0) >= 0 ? 'text-teal-500' : 'text-red-500'}`}>
                      {formatCurrency(aiAnalysis.summary?.net_worth || 0)}
                    </p>
                  </div>
                </div>

                {/* Monthly Payments Breakdown */}
                {aiAnalysis.monthly_payments_breakdown && aiAnalysis.monthly_payments_breakdown.length > 0 && (
                  <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-amber-500" />
                      Monthly Payments Breakdown
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-zinc-400 text-left border-b border-white/10">
                            <th className="pb-2">Name</th>
                            <th className="pb-2">Type</th>
                            <th className="pb-2 text-right">Balance</th>
                            <th className="pb-2 text-right">Rate</th>
                            <th className="pb-2 text-right">Payment</th>
                            <th className="pb-2 text-right">Interest</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {aiAnalysis.monthly_payments_breakdown.map((item, idx) => (
                            <tr key={idx} className="text-white">
                              <td className="py-2 font-medium">{item.name}</td>
                              <td className="py-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  item.type === 'mortgage' ? 'bg-purple-500/20 text-purple-400' :
                                  item.type === 'loan' ? 'bg-blue-500/20 text-blue-400' :
                                  item.type === 'credit_card' ? 'bg-red-500/20 text-red-400' :
                                  item.type === 'property_expense' ? 'bg-emerald-500/20 text-emerald-400' :
                                  item.type === 'dashboard_expense' ? 'bg-orange-500/20 text-orange-400' :
                                  item.type === 'insurance' ? 'bg-cyan-500/20 text-cyan-400' :
                                  'bg-zinc-500/20 text-zinc-400'
                                }`}>
                                  {item.type === 'credit_card' ? 'Credit Card' : 
                                   item.type === 'property_expense' ? 'Property Expense' :
                                   item.type === 'dashboard_expense' ? 'Dashboard Expense' :
                                   item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                                </span>
                              </td>
                              <td className="py-2 text-right font-mono text-zinc-300">
                                {item.balance > 0 ? formatCurrency(item.balance) : '-'}
                              </td>
                              <td className="py-2 text-right font-mono text-zinc-300">
                                {item.rate > 0 ? `${item.rate.toFixed(2)}%` : '-'}
                              </td>
                              <td className="py-2 text-right font-mono text-amber-400">
                                {formatCurrency(item.monthly_payment)}
                              </td>
                              <td className="py-2 text-right font-mono text-orange-400">
                                {item.monthly_interest > 0 ? formatCurrency(item.monthly_interest) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t border-white/20">
                          <tr className="font-bold text-white">
                            <td colSpan="4" className="pt-2 text-right">TOTALS:</td>
                            <td className="pt-2 text-right font-mono text-amber-500">
                              {formatCurrency(aiAnalysis.summary?.total_monthly_debt_payment || 0)}
                            </td>
                            <td className="pt-2 text-right font-mono text-orange-500">
                              {formatCurrency(aiAnalysis.summary?.total_monthly_interest || 0)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Debt Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      Debt Breakdown
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400 flex items-center gap-2">
                          <Wallet className="w-4 h-4" /> Loans
                        </span>
                        <span className="font-mono text-red-400">{formatCurrency(aiAnalysis.debts?.loans?.total_balance || 0)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400 flex items-center gap-2">
                          <CreditCard className="w-4 h-4" /> Credit Cards
                        </span>
                        <span className="font-mono text-red-400">{formatCurrency(aiAnalysis.debts?.credit_cards?.total_balance || 0)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400 flex items-center gap-2">
                          <Home className="w-4 h-4" /> Mortgages/HELOCs
                        </span>
                        <span className="font-mono text-red-400">
                          {formatCurrency(aiAnalysis.debts?.mortgages?.total_balance || 0)}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-white/10 flex justify-between text-sm">
                        <span className="text-zinc-400">Insurance Premiums (monthly)</span>
                        <span className="font-mono text-amber-400">{formatCurrency(aiAnalysis.debts?.insurance_premiums?.monthly_payment || 0)}</span>
                      </div>
                      {aiAnalysis.debts?.property_expenses?.monthly_total > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400 flex items-center gap-2">
                            <Building2 className="w-4 h-4" /> Property Expenses (monthly)
                          </span>
                          <span className="font-mono text-emerald-400">{formatCurrency(aiAnalysis.debts?.property_expenses?.monthly_total || 0)}</span>
                        </div>
                      )}
                      {aiAnalysis.debts?.dashboard_expenses?.monthly_total > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400 flex items-center gap-2">
                            <Wallet className="w-4 h-4" /> Dashboard Expenses (monthly)
                          </span>
                          <span className="font-mono text-orange-400">{formatCurrency(aiAnalysis.debts?.dashboard_expenses?.monthly_total || 0)}</span>
                        </div>
                      )}
                      {aiAnalysis.debts?.dashboard_expenses?.total_all_expenses > 0 && (
                        <div className="flex justify-between text-sm pt-2 border-t border-white/10 mt-2">
                          <span className="text-zinc-400 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> Total Recorded Expenses
                          </span>
                          <span className="font-mono text-red-400">{formatCurrency(aiAnalysis.debts?.dashboard_expenses?.total_all_expenses || 0)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      Income Breakdown
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400 flex items-center gap-2">
                          <Briefcase className="w-4 h-4" /> Salary
                        </span>
                        <span className="font-mono text-primary">{formatCurrency(aiAnalysis.income?.salary?.monthly_total || 0)}/mo</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400 flex items-center gap-2">
                          <Building2 className="w-4 h-4" /> Rental
                        </span>
                        <span className="font-mono text-primary">{formatCurrency(aiAnalysis.income?.rental?.monthly_total || 0)}/mo</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400 flex items-center gap-2">
                          <Target className="w-4 h-4" /> Business
                        </span>
                        <span className="font-mono text-primary">{formatCurrency(aiAnalysis.income?.business?.monthly_total || 0)}/mo</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> Investments
                        </span>
                        <span className="font-mono text-primary">{formatCurrency(aiAnalysis.income?.investments?.monthly_total || 0)}/mo</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="p-4 bg-zinc-900/50 border border-white/5 rounded">
                  <h3 className="font-semibold text-white mb-3">Key Metrics</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase">Monthly Cash Flow</p>
                      <p className={`text-xl font-bold font-mono ${(aiAnalysis.summary?.monthly_cash_flow || 0) >= 0 ? 'text-primary' : 'text-red-500'}`}>
                        {formatCurrency(aiAnalysis.summary?.monthly_cash_flow || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase">Debt-to-Income Ratio</p>
                      <p className={`text-xl font-bold font-mono ${(aiAnalysis.summary?.debt_to_income_ratio || 0) <= 36 ? 'text-primary' : (aiAnalysis.summary?.debt_to_income_ratio || 0) <= 43 ? 'text-amber-500' : 'text-red-500'}`}>
                        {aiAnalysis.summary?.debt_to_income_ratio || 0}%
                      </p>
                      <p className="text-xs text-zinc-500">
                        {(aiAnalysis.summary?.debt_to_income_ratio || 0) <= 36 ? 'Excellent' : (aiAnalysis.summary?.debt_to_income_ratio || 0) <= 43 ? 'Good' : 'High'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase">Total Assets</p>
                      <p className="text-xl font-bold font-mono text-teal-500">
                        {formatCurrency(aiAnalysis.summary?.total_assets || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Insights */}
                <div className="p-4 bg-gradient-to-r from-violet-500/10 to-primary/10 border border-primary/30 rounded">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-primary" />
                    AI Analysis & Recommendations
                  </h3>
                  <div className="prose prose-invert prose-sm max-w-none text-zinc-300">
                    <ReactMarkdown>{aiAnalysis.ai_analysis || "No analysis available"}</ReactMarkdown>
                  </div>
                </div>

                <p className="text-xs text-zinc-500 text-center">
                  Generated on {new Date(aiAnalysis.generated_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <p className="text-zinc-400 text-center py-8">No analysis available</p>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
